import { useEffect, useRef, type ElementType, type ReactNode } from 'react';

interface RevealProps {
  children: ReactNode;
  /** Stagger, in ms, applied as a CSS custom property. */
  delay?: number;
  /** Fraction of the element that must be visible before it plays. */
  amount?: number;
  as?: ElementType;
  className?: string;
  style?: React.CSSProperties;
  /** Passed through to the rendered element (tabIndex, role, aria-*). */
  [key: `aria-${string}`]: unknown;
  tabIndex?: number;
  role?: string;
}

/**
 * Plays an element in the first time it enters the viewport.
 *
 * The hidden state lives in CSS behind `.js` (see design.css), so the
 * prerendered HTML is fully visible to crawlers and to anyone without
 * JavaScript — the animation is an enhancement, never a gate on the content.
 *
 * It observes once and disconnects: re-animating on every scroll-by is the
 * thing that makes long pages feel restless.
 */
export function Reveal({
  children,
  delay = 0,
  amount = 0.18,
  as: Tag = 'div',
  className,
  style,
  ...rest
}: RevealProps) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (!('IntersectionObserver' in window)) {
      el.setAttribute('data-reveal', 'in');
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.setAttribute('data-reveal', 'in');
          observer.unobserve(entry.target);
        }
      },
      { threshold: amount, rootMargin: '0px 0px -8% 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [amount]);

  return (
    <Tag
      ref={ref}
      data-reveal=""
      className={className}
      style={{ ...style, ['--reveal-delay' as string]: `${delay}ms` }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
